/**
 * LoadError — the catalogue could not be fetched, and here is the way out.
 *
 * Every list on this surface needs three states, and the shipped pages had a
 * thin version of the third: a muted box with one sentence and an outline
 * "Повторить". Two things were missing. It never said WHAT failed in terms the
 * user could act on, and — because the user may well have scrolled past it —
 * nothing surfaced the failure anywhere else. The pages now also fire a
 * `notify.error` carrying the same retry, which is the app-wide rule for
 * errors: an error the user cannot act on is a dead end.
 *
 * Visually it is a slip, not an alert bar: a hairline frame, an error-toned
 * mark, the message, and the recovery. Deliberately NOT a thick coloured
 * left border — that is a banned decoration here, and a 1px frame plus an
 * icon carries the same meaning without the stripe.
 *
 * The retry button is a real 44px control because on a bazaar connection this
 * is a button people press repeatedly, standing up, one-handed.
 */
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { RotateCw, TriangleAlert } from "lucide-react";

import { iconProps } from "@/lib/icons";
import { MOTION } from "@/lib/motion";
import { useGardenMotion } from "@/lib/use-garden-motion";

export interface LoadErrorProps {
  /** What failed, in the user's words. */
  message: string;
  onRetry: () => void;
  testId: string;
  retryTestId: string;
  /** Overrides the default "Retry" label when a surface has its own key. */
  retryLabel?: string;
}

export function LoadError({
  message,
  onRetry,
  testId,
  retryTestId,
  retryLabel,
}: LoadErrorProps): ReactElement {
  const { t } = useTranslation("marketplace");
  const { reduced } = useGardenMotion();

  return (
    <motion.div
      role="alert"
      data-testid={testId}
      className="flex flex-col items-start gap-3 rounded-[var(--g-radius)] border border-[color:var(--g-rule)] bg-card p-5 text-card-foreground"
      initial={reduced ? false : { opacity: 0, y: 8 }}
      animate={reduced ? undefined : { opacity: 1, y: 0 }}
      transition={
        reduced
          ? undefined
          : { duration: MOTION.dur.bubble, ease: MOTION.ease.out }
      }
    >
      <p className="flex items-start gap-2 text-sm font-medium text-foreground">
        <TriangleAlert
          {...iconProps("sm")}
          className="mt-0.5 shrink-0 text-[color:var(--g-error)]"
        />
        {message}
      </p>
      <button
        type="button"
        onClick={onRetry}
        data-testid={retryTestId}
        className="inline-flex min-h-[44px] items-center gap-2 rounded-[10px] border border-[color:var(--g-rule-strong)] bg-[color:var(--g-sunken)] px-4 text-sm font-semibold text-foreground transition-colors hover:border-[color:var(--g-primary)] hover:text-[color:var(--g-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <RotateCw {...iconProps("sm")} />
        {retryLabel ?? t("browse.retry")}
      </button>
    </motion.div>
  );
}
